/**
 * Sample Ordering Page JavaScript
 * Handles filtering, search, cart management, and UI interactions
 */

class SampleOrderingPage extends HTMLElement {
  constructor() {
    super();

    this.materialFilter = 'all';
    this.colorFilter = 'all';
    this.searchQuery = '';
    this.colorFamilies = {};

    // Bind methods
    this._onFilterChange = this._onFilterChange.bind(this);
    this._onSearchInput = this._onSearchInput.bind(this);
    this._onAddToCart = this._onAddToCart.bind(this);
    this._onRemoveFromCart = this._onRemoveFromCart.bind(this);
    this._onCartChange = this._onCartChange.bind(this);
  }

  connectedCallback() {
    // Load color family data
    this._loadColorFamilies();

    // Initialize elements
    this.filterSelects = this.querySelectorAll('.sample-filter-select');
    this.searchInput = this.querySelector('.sample-search-input');
    this.swatchItems = this.querySelectorAll('.sample-swatch-item');
    this.noResultsMessage = this.querySelector('.sample-ordering__no-results');
    this.countDisplay = this.querySelector('.sample-count');
    this.visibleCountDisplay = this.querySelector('.visible-count');
    this.totalPriceDisplay = this.querySelector('.sample-total-price');
    this.resetButton = this.querySelector('.sample-reset-filters');
    this.stickyBar = this.querySelector('.sample-ordering__sticky-bar');
    this.addButtons = this.querySelectorAll('.sample-swatch-item__add-btn');

    // Add event listeners for dropdown selects
    this.filterSelects.forEach(select => {
      select.addEventListener('change', this._onFilterChange);
    });

    if (this.searchInput) {
      this.searchInput.addEventListener('input', this._debounce(this._onSearchInput, 200));
    }

    if (this.resetButton) {
      this.resetButton.addEventListener('click', () => this._resetFilters());
    }

    // Add click handlers to all add buttons
    this.addButtons.forEach(btn => {
      btn.addEventListener('click', this._onAddToCart);
    });

    // Add click handlers to all remove buttons (checkmarks)
    this.removeButtons = this.querySelectorAll('.sample-swatch-item__added');
    this.removeButtons.forEach(btn => {
      btn.addEventListener('click', this._onRemoveFromCart);
    });

    // Listen for cart events
    document.addEventListener('cart:change', this._onCartChange);

    // Initialize cart state (count + in-cart indicators)
    this._initializeCartState();
  }

  disconnectedCallback() {
    document.removeEventListener('cart:change', this._onCartChange);
  }

  _loadColorFamilies() {
    const sectionId = this.dataset.sectionId;
    const dataEl = document.getElementById(`color-families-data-${sectionId}`);

    if (dataEl) {
      try {
        this.colorFamilies = JSON.parse(dataEl.textContent);
        // Clean up the color values
        Object.keys(this.colorFamilies).forEach(family => {
          this.colorFamilies[family] = this.colorFamilies[family]
            .map(c => c.trim().toLowerCase())
            .filter(c => c);
        });
      } catch (e) {
        console.error('Failed to parse color families:', e);
        this.colorFamilies = {};
      }
    }
  }

  async _initializeCartState() {
    try {
      const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart.js`);
      const cart = await response.json();
      this._updateCartDisplay(cart);
      this._updateInCartStates(cart);
    } catch (e) {
      console.error('Failed to fetch cart:', e);
    }
  }

  _onFilterChange(event) {
    const select = event.currentTarget;
    const filterGroup = select.closest('[data-filter-type]');
    const filterType = filterGroup?.dataset.filterType;
    const filterValue = select.value;

    // Update filter state
    if (filterType === 'material') {
      this.materialFilter = filterValue;
    } else if (filterType === 'color') {
      this.colorFilter = filterValue;
    }

    this._applyFilters();
  }

  _onSearchInput(event) {
    this.searchQuery = event.target.value.toLowerCase().trim();
    this._applyFilters();
  }

  _applyFilters() {
    let visibleCount = 0;

    this.swatchItems.forEach(item => {
      const material = item.dataset.material;
      const colorName = item.dataset.colorName || '';
      const variantName = item.dataset.variantName || '';

      // Material filter
      const matchesMaterial =
        this.materialFilter === 'all' ||
        material === this.materialFilter;

      // Color filter
      const matchesColor = this._matchesColorFamily(colorName);

      // Search filter
      const matchesSearch =
        !this.searchQuery ||
        variantName.includes(this.searchQuery) ||
        colorName.includes(this.searchQuery);

      const isVisible = matchesMaterial && matchesColor && matchesSearch;
      item.hidden = !isVisible;

      if (isVisible) visibleCount++;
    });

    // Update visible count
    if (this.visibleCountDisplay) {
      this.visibleCountDisplay.textContent = visibleCount;
    }

    // Show/hide no results message
    if (this.noResultsMessage) {
      this.noResultsMessage.hidden = visibleCount > 0;
    }
  }

  _matchesColorFamily(colorName) {
    if (this.colorFilter === 'all') return true;
    if (!this.colorFamilies || !this.colorFamilies[this.colorFilter]) return true;

    const familyColors = this.colorFamilies[this.colorFilter];
    const normalizedColor = colorName.toLowerCase().trim();

    // Check if color name contains any of the family color keywords
    return familyColors.some(familyColor =>
      normalizedColor.includes(familyColor) ||
      familyColor.includes(normalizedColor)
    );
  }

  _resetFilters() {
    this.materialFilter = 'all';
    this.colorFilter = 'all';
    this.searchQuery = '';

    // Reset select dropdowns
    this.filterSelects.forEach(select => {
      select.value = 'all';
    });

    if (this.searchInput) {
      this.searchInput.value = '';
    }

    this._applyFilters();
  }

  async _onAddToCart(event) {
    const button = event.currentTarget;
    const variantId = button.dataset.variantId;

    if (!variantId || button.disabled) return;

    // Find the parent swatch item
    const swatchItem = button.closest('.sample-swatch-item');
    const addedIndicator = swatchItem?.querySelector('.sample-swatch-item__added');

    // Set loading state
    button.classList.add('is-loading');
    button.setAttribute('aria-busy', 'true');

    try {
      const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart/add.js`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          items: [{
            id: parseInt(variantId, 10),
            quantity: 1
          }]
        })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.description || 'Failed to add to cart');
      }

      // Fetch updated cart and dispatch event
      const cartResponse = await fetch(`${window.Shopify?.routes?.root || '/'}cart.js`);
      const cart = await cartResponse.json();

      // Dispatch cart change event for our component only (don't open cart drawer)
      // Using a custom event name to avoid triggering theme's cart drawer
      document.dispatchEvent(new CustomEvent('cart:change', {
        detail: { cart, source: 'sample-ordering' }
      }));

    } catch (error) {
      console.error('Add to cart error:', error);
      // Could show an error message here
      alert(error.message || 'Could not add item to cart. Please try again.');
    } finally {
      button.classList.remove('is-loading');
      button.removeAttribute('aria-busy');
    }
  }

  async _onRemoveFromCart(event) {
    const button = event.currentTarget;
    const variantId = button.dataset.variantId;

    if (!variantId) return;

    // Set loading state
    button.classList.add('is-loading');
    button.setAttribute('aria-busy', 'true');

    try {
      const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart/change.js`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          id: variantId,
          quantity: 0
        })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.description || 'Failed to remove from cart');
      }

      const cart = await response.json();

      // Dispatch cart change event
      document.dispatchEvent(new CustomEvent('cart:change', {
        detail: { cart, source: 'sample-ordering' }
      }));

    } catch (error) {
      console.error('Remove from cart error:', error);
      alert(error.message || 'Could not remove item from cart. Please try again.');
    } finally {
      button.classList.remove('is-loading');
      button.removeAttribute('aria-busy');
    }
  }

  _onCartChange(event) {
    const cart = event.detail?.cart;
    if (cart) {
      this._updateCartDisplay(cart);
      this._updateInCartStates(cart);
    }
  }

  _updateCartDisplay(cart) {
    if (!cart) return;

    // Filter items belonging to this sample product
    const currentProductId = parseInt(this.dataset.productId, 10);
    let sampleCount = 0;
    let sampleTotal = 0;

    if (cart.items) {
      cart.items.forEach(item => {
        if (item.product_id === currentProductId) {
          sampleCount += item.quantity;
          sampleTotal += item.final_line_price;
        }
      });
    } else {
      // Fallback if items not available (shouldn't happen with /cart.js)
      sampleCount = cart.item_count || 0;
      sampleTotal = cart.total_price || 0;
    }

    if (this.countDisplay) {
      this.countDisplay.textContent = sampleCount;
    }

    if (this.totalPriceDisplay) {
      // Format money (basic implementation, ideally use a currency formatter)
      const formattedTotal = '$' + (sampleTotal / 100).toFixed(0);
      this.totalPriceDisplay.textContent = `(${formattedTotal})`;
    }

    // Show/hide sticky bar based on count
    if (this.stickyBar) {
      this.stickyBar.classList.toggle('is-visible', sampleCount > 0);
    }
  }

  _updateInCartStates(cart) {
    if (!cart || !cart.items) return;

    // Get all variant IDs currently in cart
    const cartVariantIds = new Set(cart.items.map(item => String(item.variant_id)));

    // Update each swatch item
    this.swatchItems.forEach(item => {
      const variantId = item.dataset.variantId;
      const addBtn = item.querySelector('.sample-swatch-item__add-btn');
      const addedIndicator = item.querySelector('.sample-swatch-item__added');

      if (cartVariantIds.has(variantId)) {
        // Variant is in cart - show "in cart" state
        item.classList.add('is-in-cart');
        if (addBtn) addBtn.hidden = true;
        if (addedIndicator) addedIndicator.hidden = false;
      } else {
        // Variant not in cart - show add button
        item.classList.remove('is-in-cart');
        if (addBtn) addBtn.hidden = false;
        if (addedIndicator) addedIndicator.hidden = true;
      }
    });
  }

  _debounce(fn, delay) {
    let timer = null;
    return (...args) => {
      clearTimeout(timer);
      timer = setTimeout(() => fn.apply(this, args), delay);
    };
  }
}

// Register custom element
if (!customElements.get('sample-ordering-page')) {
  customElements.define('sample-ordering-page', SampleOrderingPage);
}
